import { TaxResult } from '../../../utils/TaxEngine';

interface ResultsPanelProps {
  taxResult: TaxResult;
  frequency: 'annual' | 'monthly' | 'biweekly';
  currencySymbol: string;
  formatCurrency: (amount: number) => string;
}

const ResultsPanel = ({ taxResult, frequency, currencySymbol, formatCurrency }: ResultsPanelProps) => {
  const getFrequencyDivisor = () => {
    if (frequency === 'monthly') return 12;
    if (frequency === 'biweekly') return 26;
    return 1;
  };

  const divisor = getFrequencyDivisor();
  const displayNet = taxResult.netSalary / divisor;

  const getFrequencyLabel = () => {
    if (frequency === 'monthly') return 'per month';
    if (frequency === 'biweekly') return 'per paycheck';
    return 'per year';
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-xl p-8 text-white sticky top-24">
      <div className="mb-8">
        <p className="text-sm text-gray-400 mb-2">Your Take-Home Pay</p>
        <p className="text-5xl font-bold mb-1">{formatCurrency(displayNet)}</p>
        <p className="text-sm text-gray-400">{getFrequencyLabel()}</p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between py-3 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xl text-green-400"></i>
            </div>
            <span className="text-sm text-gray-300">Gross Salary</span>
          </div>
          <span className="font-semibold">{formatCurrency(taxResult.grossSalary / divisor)}</span>
        </div>

        {taxResult.breakdown.map((item, index) => (
          <div key={index} className="flex items-center justify-between py-3 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${getIconForCategory(item.category)} text-xl text-red-400`}></i>
              </div>
              <span className="text-sm text-gray-300">{item.category}</span>
            </div>
            <span className="font-semibold text-red-400">-{formatCurrency(item.amount / divisor)}</span>
          </div>
        ))}

        <div className="flex items-center justify-between py-4 border-t-2 border-gray-600 mt-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-wallet-3-line text-xl text-blue-400"></i>
            </div>
            <span className="font-semibold">Net Pay</span>
          </div>
          <span className="text-2xl font-bold text-blue-400">{formatCurrency(displayNet)}</span>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 mt-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Effective Tax Rate</span>
            <span className="font-semibold text-yellow-400">
              {(taxResult.effectiveRate * 100).toFixed(2)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const getIconForCategory = (category: string): string => {
  const icons: { [key: string]: string } = {
    'Federal Tax': 'ri-government-line',
    'State Tax': 'ri-map-pin-line',
    'Local Tax': 'ri-community-line',
    'Social Security': 'ri-shield-check-line',
    'Medicare': 'ri-heart-pulse-line',
    'Income Tax': 'ri-file-list-3-line',
    'National Insurance': 'ri-shield-user-line'
  };
  return icons[category] || 'ri-subtract-line';
};

export default ResultsPanel;